/**
 * THE point-in-polygon primitive for the X-Z plane (issue #1030).
 *
 * One even-odd ray-cast implementation, two deliberate boundary
 * semantics — pick one instead of copying the loop:
 *
 *   - `isPointInsidePolygon(point, polygon)` — STRICT/EXCLUSIVE. A point
 *     exactly on an edge may return either result (the strict `<` makes
 *     it arbitrary, depending on edge direction). Use for offset probes
 *     and interior samples, where the caller guarantees the point isn't
 *     edge-exact.
 *   - `isPointInsideOrOnPolygon(point, polygon, tolerance)` — INCLUSIVE.
 *     Edge/vertex points (within `tolerance`) count as inside. Use when
 *     scan geometry can put the tested point exactly on the boundary —
 *     a cabinet edge routinely lands on a room polygon edge.
 *
 * `polygon` is an `{x, z}[]` ring, either winding, not necessarily
 * closed (last→first edge is implied). No input guards: a degenerate
 * polygon is a data bug the caller must handle, not something to paper
 * over with `false`.
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const { hypot2 } = require('./hypot2');

function isPointInsidePolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const zi = polygon[i].z;
    const xj = polygon[j].x;
    const zj = polygon[j].z;
    if (
      zi > point.z !== zj > point.z &&
      point.x < ((xj - xi) * (point.z - zi)) / (zj - zi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function isPointInsideOrOnPolygon(point, polygon, tolerance) {
  if (isPointInsidePolygon(point, polygon)) {
    return true;
  }
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    if (distanceToLineSegment(point, polygon[j], polygon[i]) <= tolerance) {
      return true;
    }
  }
  return false;
}

/** Shortest distance from `point` to the segment p1–p2, in the X-Z plane. */
function distanceToLineSegment(point, p1, p2) {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) {
    return hypot2(point.x - p1.x, point.z - p1.z);
  }
  let t = ((point.x - p1.x) * dx + (point.z - p1.z) * dz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return hypot2(point.x - (p1.x + t * dx), point.z - (p1.z + t * dz));
}

module.exports = {
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,
};
